/*
Every user-facing string of the XOL-596 save / conflict / command flow.

The UX spec (docs/plans/2026-10-06-xol-596-stale-workflow-saves.md, "## UX spec")
gives each string a stable dotted key. This module is the one place they live, so
specs can assert exact text and a copy change is one edit. The app has no i18n
framework (XOL-158): these are plain English strings and small template
functions. ES5, no optional chaining or nullish coalescing.

Usage:

    import { copyText, fieldLabel } from '@/lib/requestSyncCopy'

    copyText('requestSync.status.saving')                 // 'Saving…'
    copyText('requestSync.stale.title', { n: 2 })         // '2 of your changes weren't saved'
    fieldLabel('step', 'state.reviseTP')                  // 'Revise Technical Proposal'

A template function takes ONE params object. Dates and times arrive already
formatted (the caller owns `formatDate` / `formatDateTime`). Placeholders in the
spec map to params like this:

    {User name}  by        {org}   org         {Tower Co}  towerCo
    {Telco}      telco     {Provider} provider {RTx}       rtx
    {id}         id        {n}     n           {date}      date
    {datetime}   datetime  {label} label

Never show a raw path, code or JSON value: labels come from `fieldLabel`, and an
unmapped path reads "Other details" with the value "Changed".
------------------------------------------------------------------------ */

function plural (n, one, many) {
  return n === 1 ? one : many
}

function who (p) {
  return p.by ? p.by + (p.org ? ' (' + p.org + ')' : '') : null
}

/* ------------------------------------------------------------------------
   C-3: field labels, keyed by PATH (requestSync.field.*)
------------------------------------------------------------------------ */

var STEP_LABELS = {
  'remarks': function (p) {
    // A PRG progress step's one editor is headed "General Remarks" (XOL-596 slice 4).
    if (p.form === 'progressStep') {
      return 'General Remarks'
    }
    // Approval Remarks on party-1 step 2 or 5, otherwise Progress Remarks.
    var approval = Number(p.partyNo) === 1 && (Number(p.stepNo) === 2 || Number(p.stepNo) === 5)
    return approval ? 'Approval Remarks' : 'Progress Remarks'
  },
  'status': 'Status',
  'priority': 'Priority',
  // XOL-596 (decision fields): the approval controls save like fields. Step 5's screen
  // says "Approval"; "Approval Status" is what step 2 shows and reads clearly in
  // "Saved: Approval Status." (PRG reports its own "Approval" and never reaches these).
  'approvedStatus': function (p) {
    return p.form === 'progressStep' ? 'Approval' : 'Approval Status'
  },
  'approved_at': function (p) {
    return p.form === 'progressStep' ? 'Review Date' : 'Approval Date'
  },
  // The PRG progress steps' approval switch: reported as saved once its command
  // went through (XOL-596 slice 4).
  'approved': 'Approval',
  // The PRG Save result "Approval locked" (a lock that went through with the Save).
  'approvalLocked': 'Approval locked',
  'state.reviseTP': 'Revise Technical Proposal',
  'state.reSurvey': 'Re-survey',
  'state.allowClientEquipmentInput': 'Allow Telcos to Input Equipment',
  'state.serviceType': 'Service Type',
  'state.spectrumBand': 'Spectrum Band',
  'state.tpType': 'Technical Proposal type',
  'state.siteId': 'Telco Site ID',
  'state.approvalCME': 'CME approval',
  'state.approvedCME': 'CME approval date',
  'state.depostiReturnedCME': 'CME deposit returned',
  'state.approvalTI': 'TI approval',
  'state.approvedTI': 'TI approval date',
  'state.depostiReturnedTI': 'TI deposit returned',
  'state.approvalMW': 'MW approval',
  'state.approvedMW': 'MW approval date',
  'state.depostiReturnedMW': 'MW deposit returned',
  'state.approvalPTW': 'Permit to Work approval',
  'state.approvedPTW': 'Permit to Work approval date',
  'state.depostiReturnedPTW': 'Permit to Work deposit returned',
  'state.depositRequired': 'Overall Deposit Required',
  'state.paid': 'Deposit paid',
  'state.deposit': 'Deposit amount'
}

var ACTION_LABELS = {
  'remarks': 'General Remarks',
  'summary': 'Summary',
  'priority': 'Priority',
  'targetDate': 'Target Date',
  'actualDate': 'Actual Date',
  'actionBy': 'Action By',
  'status': 'Status',
  'state.step2.equipment': 'Equipment',
  'state.step3.docType': 'Agreement Type',
  // The step-3 collapse title the user opened (its typo fixed in the card only).
  'state.step3.legalApproved': 'Finance Approval of Legal Document',
  'state.step5.depositReturned': 'Deposit Returned',
  'state.step5.amount': 'Deposit Returned amount',
  'state.step5.resubmitRemarks': 'Re-submission Remarks',
  'emails.tower': function (p) {
    return p.towerCo + ' e-mail recipients'
  },
  'emails.telco': function (p) {
    return p.telco + ' e-mail recipients'
  },
  'emails.sendMail': 'Send e-mails'
}

/**
 * The label of a path.
 *
 * @param {String} kind  'step' | 'action'
 * @param {String} path
 * @param {Object} ctx   { partyNo, stepNo } for a step's remarks; { towerCo,
 *                       telco } for the e-mail recipient lists
 * @return {String} the label, or "Other details" for an unmapped path
 */
export function fieldLabel (kind, path, ctx) {
  var map = kind === 'step' ? STEP_LABELS : ACTION_LABELS
  var label = Object.prototype.hasOwnProperty.call(map, path) ? map[path] : null

  if (label === null) {
    return 'Other details'
  }

  return typeof label === 'function' ? label(ctx || {}) : label
}

/* ------------------------------------------------------------------------
   The copy table: { 'requestSync.<group>.<key>': string | function (p) }
------------------------------------------------------------------------ */

var COPY = {

  // -- C-4 row labels --------------------------------------------------
  'requestSync.rowLabel.step': function (p) {
    return p.rtx + ' #' + p.id + ' · Step ' + p.n + ': ' + p.title + (p.org ? ' (' + p.org + ')' : '')
  },
  'requestSync.rowLabel.action': function (p) {
    return p.rtx + ' #' + p.id + ' · Request details'
  },
  'requestSync.rowLabel.actionDetails': function (p) {
    return p.rtx + ' #' + p.id + ' · Action details'
  },
  'requestSync.rowLabel.progressStep': function (p) {
    return p.rtx + ' #' + p.id + ' · Step ' + p.n
  },

  // -- C-3 fallbacks -----------------------------------------------------
  'requestSync.field.other': 'Other details',
  'requestSync.field.otherValue': 'Changed',

  // -- C-1 notice body ---------------------------------------------------
  'requestSync.notice.changedBy': function (p) {
    var name = who(p)
    return name
      ? 'Changed by ' + name + (p.datetime ? ' on ' + p.datetime : '')
      : 'Changed by someone else'
  },
  'requestSync.notice.savedLine': 'Anything not listed here was saved.',
  'requestSync.notice.yours': 'Yours:',
  'requestSync.notice.savedNow': 'Saved now:',
  'requestSync.notice.added': 'Added:',
  'requestSync.notice.removed': 'Removed:',
  'requestSync.notice.dismiss': 'Dismiss',
  'requestSync.notice.retry': 'Try again',
  'requestSync.notice.empty': '(empty)',
  'requestSync.notice.yes': 'Yes',
  'requestSync.notice.no': 'No',

  // -- Item 1: the burst popup ------------------------------------------
  // 1-3 fields list them; more than 3 count them. `prefix` ("{RTx} #{id} — ")
  // only when that request's drawer is no longer open or the burst spans more
  // than one request.
  'requestSync.saved.fields': function (p) {
    var labels = p.labels || []
    var prefix = p.rtx ? p.rtx + ' #' + p.id + ' — ' : ''
    return labels.length > 3
      ? prefix + 'Saved ' + labels.length + ' changes.'
      : prefix + 'Saved: ' + labels.join(', ') + '.'
  },
  'requestSync.saved.prefix': function (p) {
    return p.rtx + ' #' + p.id + ' — '
  },

  // -- Item 2: 409 stale_field -------------------------------------------
  'requestSync.stale.title': function (p) {
    return p.n === 1 || p.n === undefined
      ? 'Your change wasn\'t saved'
      : p.n + ' of your changes weren\'t saved'
  },
  'requestSync.stale.lead.named': function (p) {
    return who(p) + ' changed ' + (p.n > 1 ? 'these fields' : 'this field') +
      ' while you were editing. Their version is now shown, and yours below was not saved.'
  },
  'requestSync.stale.lead.self': function (p) {
    return 'You changed ' + (p.n > 1 ? 'these fields' : 'this field') +
      ' in another tab or window. That version is now shown, and the change below was not saved.'
  },
  'requestSync.stale.lead.unknown': function (p) {
    return 'Someone else changed ' + (p.n > 1 ? 'these fields' : 'this field') +
      ' while you were editing. Their version is now shown, and yours below was not saved.'
  },
  'requestSync.stale.footer': 'Re-enter anything you still need — it will save as usual.',
  'requestSync.stale.footer.equipment': 'Add or remove those rows again if you still need to.',
  'requestSync.stale.footer.recipients': 'Add or remove those addresses again if you still need to.',

  // -- Decision fields (XOL-596, 2026-10-14): a decision refused with 409 stale_decision.
  // The C-1 block ("Yours" / "Saved now") and the changedBy line follow the lead, so the
  // lead gives the facts only. `kind` 'status' (steps 2, 5) or 'legal' (step 3).
  'requestSync.decision.stale.lead': function (p) {
    var who = p.by || 'Someone else'
    if (p.kind === 'legal') {
      var doc = p.docType || 'legal documents'
      if (p.self) {
        return 'You changed the ' + doc + ' approval in another tab or window. That is now shown, and the change below was not saved.'
      }
      return p.approved
        ? who + ' approved the ' + doc + ' while you were editing. That is now shown, and yours below was not saved.'
        : who + ' removed the ' + doc + ' approval while you were editing. That is now shown, and yours below was not saved.'
    }
    if (p.self) {
      return 'You set Step ' + p.n + ' to ' + (p.status || 'Pending') + ' in another tab or window. That decision is now shown, and the change below was not saved.'
    }
    if (!p.status || p.status === 'Pending') {
      return who + ' reset Step ' + p.n + '\'s approval to Pending while you were editing. That is now shown, and yours below was not saved.'
    }
    return who + ' set Step ' + p.n + ' to ' + p.status + ' while you were editing. That decision is now shown, and yours below was not saved.'
  },
  'requestSync.decision.stale.footer': 'Choose your decision again if you still need it — it will save as usual.',
  'requestSync.decision.stale.footerLegal': 'Tick or untick it again if you still need to — it will save as usual.',

  // -- Copy button -------------------------------------------------------
  'requestSync.copy.button': 'Copy',
  'requestSync.copy.done': 'Copied',
  'requestSync.copy.failed': 'Couldn\'t copy — select the text in the box and copy it yourself.',
  'requestSync.copy.ariaYours': function (p) {
    return 'Copy your ' + p.label
  },
  'requestSync.copy.ariaTheirs': function (p) {
    return 'Copy their ' + p.label
  },

  // -- Item 3: 403 locks on a content save -------------------------------
  'requestSync.lock.title': 'Not saved',
  'requestSync.lock.equipmentRow': 'You can only change equipment rows your organisation added.',
  'requestSync.lock.equipmentOff': function (p) {
    return p.towerCo + ' has turned off equipment input for this request, so your change wasn\'t saved.'
  },
  'requestSync.lock.equipmentStep': function (p) {
    return 'Step 2 is now ' + (p.approved ? 'approved' : 'complete') + ', so equipment can\'t be changed.'
  },
  'requestSync.lock.stepComplete': function (p) {
    return p.towerCo + ' has completed this step, so it\'s locked until it\'s re-opened. Your change wasn\'t saved.'
  },
  'requestSync.lock.tpApproved': 'The Technical Proposal has been approved, so its type and Telco Site ID are locked. Your change wasn\'t saved.',
  'requestSync.lock.fallback': function (p) {
    return p.message || 'You can\'t change this field any more. Your change wasn\'t saved.'
  },

  // -- Item 4: network or 5xx --------------------------------------------
  'requestSync.network.title': 'Not saved — connection problem',
  'requestSync.network.lead': function (p) {
    return (p.labels || []).join(', ') + ' couldn\'t be saved because the server couldn\'t be reached. Your changes are still on screen.'
  },

  // -- Item 5: 409 on a command ------------------------------------------
  'requestSync.cmd.prgApproval.stale.title': 'Your approval change wasn\'t applied',
  'requestSync.cmd.prgApproval.stale.body': 'Someone else changed this approval first. The current approval is now shown.',
  'requestSync.cmd.step.invalid.title': 'Your step change wasn\'t applied',
  'requestSync.cmd.step.invalid.body': function (p) {
    return p.completed
      ? 'Step ' + p.n + ' was already completed by ' + p.by + ' on ' + p.datetime + '. That is now shown.'
      : 'Step ' + p.n + ' is already open. That is now shown.'
  },
  'requestSync.cmd.reject.invalid.title': 'The request wasn\'t closed',
  'requestSync.cmd.reject.invalid.body': function (p) {
    return 'The Step 2 approval is now ' + p.status + ', so the request can\'t be closed as Rejected. The current approval is now shown.'
  },
  'requestSync.cmd.complete.invalid.title': function (p) {
    return 'The ' + p.rtx + ' wasn\'t completed'
  },
  'requestSync.cmd.complete.invalid.body': function (p) {
    return 'Step ' + p.n + ' is no longer approved — it is now ' + p.status + '. The current approval is now shown.'
  },
  'requestSync.cmd.fallback.title': 'Your change wasn\'t applied',
  'requestSync.cmd.fallback.body': function (p) {
    return p.message || 'This request changed before your change arrived. The latest version is now shown.'
  },

  // -- Item 6 / 7: request_closed, and a refetch that finds it closed -----
  'requestSync.closed.title': function (p) {
    return 'This request is already ' + p.status
  },
  'requestSync.closed.body': function (p) {
    var verb = p.status === 'Rejected' ? 'rejected' : 'closed'
    var by = p.by && p.date ? 'by ' + p.by + ' on ' + p.date + ' ' : ''
    return 'It was ' + verb + ' ' + by + 'while you had it open, so your change wasn\'t applied. It is now read-only.'
  },
  'requestSync.closed.awayTitle': function (p) {
    return 'This request is now ' + p.status
  },
  'requestSync.closed.away': function (p) {
    var verb = p.status === 'Rejected' ? 'rejected' : 'closed'
    var by = p.by && p.date ? 'by ' + p.by + ' on ' + p.date + ' ' : ''
    return 'It was ' + verb + ' ' + by + 'while you were away. It is now read-only.'
  },

  // -- Item 8: leaving with saves pending ---------------------------------
  'requestSync.leave.saving': 'Saving your changes…',
  'requestSync.leave.blocked': 'This page stays open until your changes are saved.',
  'requestSync.logout.title': 'Your last change wasn\'t saved',
  'requestSync.logout.lead': function (p) {
    return 'If you log out now, ' + plural(p.n, 'this change is', 'these changes are') + ' lost:'
  },
  'requestSync.logout.confirm': 'Log out anyway',
  'requestSync.logout.cancel': 'Stay',

  // -- A save that HANGS while the user is leaving (Carlos, 2026-10-06). After
  // 15 s with no answer the user is told the changes are NOT saved yet. Reused
  // by drawer close, commands (second button "Cancel") and navigation / logout.
  'requestSync.hang.title': 'Your changes haven\'t been saved yet',
  'requestSync.hang.lead': function (p) {
    return 'The server hasn\'t answered yet, so ' + plural(p.n, 'this change has', 'these changes have') + ' not been saved:'
  },
  'requestSync.hang.leaveWarning': function (p) {
    return 'If you leave now, ' + plural(p.n, 'it is', 'they are') + ' lost.'
  },
  'requestSync.hang.keepWaiting': 'Keep waiting',
  'requestSync.hang.leaveNow': 'Leave now',
  'requestSync.hang.cancel': 'Cancel',
  // A workflow COMMAND whose POST has not been answered (slice 3 review): the
  // second button stops waiting, the outcome is unknown, and a late answer is
  // still adopted.
  'requestSync.hang.stopWaiting': 'Stop waiting',
  'requestSync.hang.commandUnknown': 'The server hasn\'t answered yet, so we don\'t know whether your change was applied.',
  'requestSync.cmd.unknown.title': 'Not confirmed yet',
  'requestSync.cmd.unknown.body': 'The server hasn\'t answered, so we don\'t know whether your change was applied. It is shown as it was, and this page will update when the server answers.',
  'requestSync.hang.leaveNoList': 'Your changes haven\'t been saved yet, because the server hasn\'t answered.',

  // A close or an in-app navigation blocked by saves that fail on the network or
  // with a 5xx (the connection-problem class; Carlos, 2026-10-08: "ask the user
  // after 30 seconds of failure to see if they would like to keep trying or close
  // the window"). {n} = seconds so far (30, then 60, ...).
  'requestSync.connection.title': 'Your changes still aren\'t saved',
  'requestSync.connection.lead': function (p) {
    return 'We\'ve been trying to save for ' + p.n + ' seconds but can\'t reach the server. Keep trying, or close and lose these changes:'
  },
  'requestSync.connection.keepTrying': 'Keep trying',
  'requestSync.connection.closeWithout': 'Close without saving',

  // -- Item 9: 428 version_required --------------------------------------
  'requestSync.reload.title': 'This page is out of date',
  'requestSync.reload.lead': 'Please reload to keep working.',
  'requestSync.reload.unsaved': 'These changes were not saved. Copy anything you need before reloading — it won\'t be kept:',
  'requestSync.reload.banner': 'This page is out of date — text and field changes here are not being saved.',
  'requestSync.reload.confirm': 'Reload',
  'requestSync.reload.cancel': 'Not now',

  // -- Item 10: 422 -------------------------------------------------------
  'requestSync.readOnly.title': 'Couldn\'t save',
  'requestSync.readOnly.body': 'Something went wrong on our side, and your change wasn\'t saved. The latest version is now shown.',
  'requestSync.readOnly.reference': function (p) {
    return 'If this keeps happening, contact support and quote reference ' + p.requestId + '.'
  },

  // -- Item 11: a command that failed, and was reverted --------------------
  'requestSync.notSaved.title': 'Not saved',
  'requestSync.notSaved.complete': function (p) {
    return 'Step ' + p.n + ' wasn\'t marked complete. Please try again.'
  },
  'requestSync.notSaved.reopen': function (p) {
    return 'Step ' + p.n + ' wasn\'t re-opened. Please try again.'
  },
  'requestSync.notSaved.approval': 'The approval change wasn\'t saved. Please try again.',
  'requestSync.notSaved.reject': 'The request wasn\'t closed. Please try again.',
  'requestSync.notSaved.completeRequest': function (p) {
    return 'The ' + p.rtx + ' wasn\'t completed. Please try again.'
  },

  // -- A command whose request no longer exists (404 and the re-GET is a 404 too).
  // Not in the UX spec; added by the builder (slice 3, Decisions 2026-10-07).
  'requestSync.gone.title': 'This request no longer exists',
  'requestSync.gone.body': 'It was removed while you had it open, so your change wasn\'t applied. This drawer is closing.',

  // -- Item 12: the drawer swapped to read-only ----------------------------
  'requestSync.done.reject': function (p) {
    return p.rtx + ' #' + p.id + ' was rejected and closed.'
  },
  'requestSync.done.complete': function (p) {
    return p.rtx + ' #' + p.id + ' is complete. Status: ' + p.status + '.'
  },

  // -- Reject / Complete {RTx} confirms ------------------------------------
  'requestSync.reject.busy': 'Rejecting…',
  'requestSync.complete.confirm.title': function (p) {
    return 'Complete this ' + p.rtx + '?'
  },
  'requestSync.complete.confirm.body': function (p) {
    return 'The ' + p.rtx + ' will be marked ' + p.status + ' and can\'t be re-opened. Continue?'
  },
  'requestSync.complete.confirm.ok': 'Complete',
  'requestSync.complete.confirm.cancel': 'Cancel',
  'requestSync.complete.busy': 'Completing…',

  // -- The ordering rule (extra footer when a save that must come first failed) ------
  // Any change can be the one that failed (remarks, a Revise TP tick, a decision), and the
  // list above the footer names it, so the wording says "the changes above".
  'requestSync.ordering.notSent.reject': 'The request wasn\'t closed because the changes above weren\'t saved. Once they\'re saved, try again.',
  'requestSync.ordering.notSent.complete': function (p) {
    return 'The ' + p.rtx + ' wasn\'t completed because the changes above weren\'t saved. Once they\'re saved, try again.'
  },
  'requestSync.ordering.notSent.email': 'The approval e-mail wasn\'t sent because the changes above weren\'t saved. Once they\'re saved, send it again.',
  // Complete Step ticked while a decision was still pending, and the decision did not
  // save: the tick went back and no prompt was shown.
  'requestSync.ordering.notSent.completeStep': function (p) {
    return 'Step ' + p.n + ' wasn\'t completed because the changes above weren\'t saved. Once they\'re saved, try again.'
  },

  // -- XOL-596 approval lock (UX addendum 2026-10-13; XOL-595 plan 4.9) --------
  // Content save refused because the approval is locked (403 approval_locked).
  'requestSync.lock.approvalLocked': function (p) {
    // `self`: the viewer's own lock (a change made while their Lock was in flight).
    if (p.self) {
      return 'You locked this approval' + (p.datetime ? ' on ' + p.datetime : '') + ', so your change wasn\'t saved.'
    }
    return p.by
      ? p.by + ' locked this approval' + (p.datetime ? ' on ' + p.datetime : '') + ', so your change wasn\'t saved.'
      : 'This approval is locked, so your change wasn\'t saved.'
  },
  'requestSync.lock.approvalLockedFooter': function (p) {
    return p.canUnlock
      ? 'Unlock the approval, then re-enter your change.'
      : 'Ask an approver or a manager to unlock it if this still needs to change.'
  },

  // The prompts (ApprovalLockDialog): drawer close, Complete Step, PRG Save, Unlock.
  'requestSync.approvalLock.ask.title': function (p) {
    return p.n > 1 ? 'Lock these approvals?' : 'Lock the approval?'
  },
  'requestSync.approvalLock.ask.lead': function (p) {
    return p.n > 1
      ? 'You made these decisions on ' + p.rtx + ' #' + p.id + ', and they aren\'t locked yet:'
      : 'You made this decision on ' + p.rtx + ' #' + p.id + ', and it isn\'t locked yet:'
  },
  'requestSync.approvalLock.ask.row': function (p) {
    return 'Step ' + p.n + ': ' + p.title + ' — ' + p.decision + (p.date ? ', ' + p.date : '')
  },
  'requestSync.approvalLock.ask.explain': 'A locked approval can\'t be changed by mistake: its decision and details become read-only until an approver or a manager unlocks it.',
  'requestSync.approvalLock.ask.lock': function (p) {
    return p.n > 1 ? 'Lock all' : 'Lock'
  },
  'requestSync.approvalLock.ask.skip': 'Leave unlocked',
  'requestSync.approvalLock.ask.stay': 'Stay',
  'requestSync.approvalLock.decision.legal': function (p) {
    return (p.docType || 'Legal documents') + ' approved'
  },
  'requestSync.approvalLock.decision.prg': 'Approved',
  'requestSync.approvalLock.complete.title': 'Complete this step?',
  // XOL-596 (completed steps, Carlos 2026-10-14): nobody can change a completed step, the
  // Tower Co included, until a manager re-opens it.
  'requestSync.approvalLock.complete.body': function (p) {
    return 'Once Step ' + p.n + ' is completed, it becomes read-only for everyone, including you. Nothing in it can be changed, and no files can be uploaded, until a manager re-opens it.'
  },
  'requestSync.approvalLock.complete.lockNote': function (p) {
    return 'Its approval (' + p.decision + ') isn\'t locked yet. Lock it now? Once the step is completed, the lock can\'t be added or removed until the step is re-opened.'
  },
  'requestSync.approvalLock.complete.lock': 'Lock & complete',
  'requestSync.approvalLock.complete.skip': 'Complete without locking',
  'requestSync.approvalLock.complete.cancel': 'Cancel',
  'requestSync.approvalLock.prg.title': 'Lock the approval?',
  'requestSync.approvalLock.prg.body': 'You\'re saving this step as approved. Lock the approval now so the approval, Reviewer and Review Date can\'t be changed by mistake? An approver or a manager can unlock it later.',
  'requestSync.approvalLock.prg.lock': 'Save and lock',
  'requestSync.approvalLock.prg.skip': 'Save without locking',
  'requestSync.approvalLock.prg.cancel': 'Cancel',
  'requestSync.approvalLock.unlock.title': 'Unlock the approval?',
  'requestSync.approvalLock.unlock.body': function (p) {
    return 'Step ' + p.n + ': ' + p.title + ' (' + p.decision + ') will become editable again: ' + p.fields + ' can be changed until someone locks it again.'
  },
  'requestSync.approvalLock.fields.step2': 'the decision, its date, Revise / Re-survey and the approval remarks',
  'requestSync.approvalLock.fields.step3': function (p) {
    return (p.docType ? 'the ' + p.docType + ' approval tick' : 'the legal approval tick') + ', the Agreement Type and the Progress Remarks'
  },
  'requestSync.approvalLock.fields.step5': 'the decision, its date and the approval remarks',
  'requestSync.approvalLock.fields.prg': 'the approval, Reviewer and Review Date',
  'requestSync.approvalLock.unlock.ok': 'Unlock',
  'requestSync.approvalLock.unlock.cancel': 'Cancel',

  // The lock line under the decision.
  'requestSync.approvalLock.line.lockedBy': function (p) {
    return 'Locked by ' + p.by + (p.datetime ? ' on ' + p.datetime : '')
  },
  'requestSync.approvalLock.line.lockedMigration': 'Locked automatically (decided before approval locking was introduced)',
  'requestSync.approvalLock.line.notLocked': 'Not locked — this decision can still be changed.',
  // On a completed step the line says what is true now (the step is read-only) and
  // offers no Lock / Unlock.
  'requestSync.approvalLock.line.notLockedFrozen': 'Not locked — read-only while the step is complete.',
  'requestSync.approvalLock.line.unlock': 'Unlock',
  'requestSync.approvalLock.line.lock': 'Lock',
  'requestSync.approvalLock.line.unlockAria': function (p) {
    return 'Unlock the ' + p.title + ' approval'
  },
  'requestSync.approvalLock.line.lockAria': function (p) {
    return 'Lock the ' + p.title + ' approval'
  },
  'requestSync.approvalLock.line.unlocking': 'Unlocking…',
  'requestSync.approvalLock.line.locking': 'Locking…',
  'requestSync.approvalLock.line.unlockAfterEdit': 'Save or cancel your edit to unlock it.',
  // The visible text at xs (the title keeps the full one).
  'requestSync.approvalLock.line.lockedShort': 'Locked ·',
  'requestSync.approvalLock.line.notLockedShort': 'Not locked ·',
  // The frozen variants drop the trailing " ·": it separated the text from a button
  // that no longer renders.
  'requestSync.approvalLock.line.lockedShortFrozen': 'Locked',
  'requestSync.approvalLock.line.notLockedShortFrozen': 'Not locked',

  // The card: the close that is locking, and the results.
  'requestSync.approvalLock.locking': function (p) {
    return p.n > 1 ? 'Locking ' + p.n + ' approvals…' : 'Locking the approval…'
  },
  'requestSync.approvalLock.done.locked': function (p) {
    var steps = (p.steps || []).map(function (n) { return 'Step ' + n })
    return (p.prefix || '') + (steps.length > 1 ? 'Approvals locked: ' : 'Approval locked: ') + steps.join(', ') + '.'
  },
  'requestSync.approvalLock.done.unlocked': function (p) {
    return 'Approval unlocked: Step ' + p.n + '.'
  },
  'requestSync.approvalLock.done.completeLocked': function (p) {
    return 'Step ' + p.n + ' completed and its approval locked.'
  },

  // A drawer close in which one or more locks did not go through.
  'requestSync.approvalLock.failed.title': function (p) {
    return p.total === 1 ? 'The approval wasn\'t locked' : p.failed + ' of ' + p.total + ' approvals weren\'t locked'
  },
  'requestSync.approvalLock.failed.lockedRow': function (p) {
    return 'Locked: Step ' + p.n + ' (' + p.title + ').'
  },
  'requestSync.approvalLock.failed.notLockedRow': function (p) {
    return 'Not locked: Step ' + p.n + ' (' + p.title + ') — ' + p.reason + '.'
  },
  'requestSync.approvalLock.failed.reason.undecided': function (p) {
    return 'its approval is now ' + p.status + ', so there was nothing to lock'
  },
  'requestSync.approvalLock.failed.reason.undecidedLegal': 'its legal approval was removed, so there was nothing to lock',
  'requestSync.approvalLock.failed.reason.changed': function (p) {
    return (p.by || 'Someone') + ' changed the decision to ' + p.status + ' first'
  },
  'requestSync.approvalLock.failed.reason.completed': function (p) {
    return p.by
      ? p.by + ' completed the step first, so it can\'t be locked until it\'s re-opened'
      : 'the step was completed first, so it can\'t be locked until it\'s re-opened'
  },
  'requestSync.approvalLock.failed.reason.network': 'the server couldn\'t be reached',
  'requestSync.approvalLock.failed.reason.unknown': 'the server hasn\'t answered, so we don\'t know whether it was locked',
  // A lock that was not sent because the save that must come first failed (that save's
  // own section says why).
  'requestSync.approvalLock.failed.reason.remarks': 'your changes weren\'t saved first',
  'requestSync.approvalLock.failed.reason.other': function (p) {
    return p.message || 'the server refused it'
  },
  'requestSync.approvalLock.failed.footer': function (p) {
    return 'This page stays open. Close it again to lock ' + (p.remaining > 1 ? 'the rest' : 'it') + ' or leave ' + (p.remaining > 1 ? 'them' : 'it') + ' unlocked.'
  },
  'requestSync.approvalLock.failed.footerUnknown': 'This page will update when the server answers.',

  // PRG: the approval was saved but the lock was not.
  'requestSync.approvalLock.prgNotLocked.title': 'The approval wasn\'t locked',
  'requestSync.approvalLock.prgNotLocked.body': 'Your approval was saved, but it couldn\'t be locked. Its status is shown below the Review & Approval row; lock it from there or when you next save.',

  // What follows "locked by" on a refused change: can this viewer unlock it?
  'requestSync.approvalLock.suffix.canUnlock': ' Unlock it first if it still needs to change.',
  'requestSync.approvalLock.suffix.askUnlock': ' Ask an approver or a manager to unlock it if it still needs to change.',

  // Command notices of the lock commands and the refusals of locked approvals.
  'requestSync.cmd.lock.invalid.title': 'The approval wasn\'t locked',
  'requestSync.cmd.lock.invalid.body': function (p) {
    return 'Step ' + p.n + '\'s approval is now ' + p.status + ', so there is no decision to lock. The current approval is now shown.'
  },
  // The decision changed after the prompt opened (409 stale_decision on /lock):
  // nothing was locked. Not in the UX spec (builder, Decisions 2026-10-14).
  'requestSync.cmd.lock.stale.title': 'The approval wasn\'t locked',
  'requestSync.cmd.lock.stale.body': function (p) {
    return (p.by || 'Someone') + ' changed Step ' + p.n + '\'s decision to ' + p.status + ' first, so nothing was locked. The current approval is now shown.'
  },
  'requestSync.cmd.completeLock.invalid.title': function (p) {
    return 'Step ' + p.n + ' wasn\'t completed or locked'
  },
  'requestSync.cmd.completeLock.invalid.body': function (p) {
    return 'Step ' + p.n + '\'s approval is now ' + p.status + ', so there is no decision to lock. Nothing was changed, and the current approval is now shown.'
  },
  'requestSync.cmd.completeLock.stale.body': function (p) {
    return (p.by || 'Someone') + ' changed Step ' + p.n + '\'s decision to ' + p.status + ' first. Nothing was changed, and the current approval is now shown.'
  },
  'requestSync.cmd.prgApproval.locked.title': 'Your approval change wasn\'t applied',
  'requestSync.cmd.prgApproval.locked.body': function (p) {
    return p.by
      ? p.by + ' locked this approval' + (p.datetime ? ' on ' + p.datetime : '') + ', so it can\'t be changed. The locked approval is now shown.'
      : 'This approval is locked, so it can\'t be changed. The locked approval is now shown.'
  },
  'requestSync.notSaved.lock': 'The approval wasn\'t locked. Please try again.',
  'requestSync.notSaved.unlock': 'The approval wasn\'t unlocked. Please try again.',
  'requestSync.notSaved.completeLock': function (p) {
    return 'Step ' + p.n + ' wasn\'t completed or locked. Please try again.'
  },
  'requestSync.ordering.notSent.lock': 'The approval wasn\'t locked because the changes above weren\'t saved. Once they\'re saved, lock it again.',
  'requestSync.ordering.notSent.lockOnClose': 'The approval wasn\'t locked because the changes above weren\'t saved. Once they\'re saved, close this request again to lock it.',
  'requestSync.ordering.notSent.completeLock': function (p) {
    return 'Step ' + p.n + ' wasn\'t completed or locked because the changes above weren\'t saved. Once they\'re saved, try again.'
  },

  // -- C-12 the save-status popup (replaces the C-10 drawer line) -----------
  'requestSync.popup.label': 'Save status',
  'requestSync.status.saving': 'Saving…',
  'requestSync.status.pending': 'Changes pending',
  'requestSync.popup.pendingHint': '· saves when you pause',
  'requestSync.status.unsaved': function (p) {
    return p.n === 1 ? '1 change not saved' : p.n + ' changes not saved'
  },
  'requestSync.status.retry': 'Try again',
  // The retry strip of a blocked close (saveWait.keepTrying).
  'requestSync.popup.retryIn': function (p) {
    return 'Retrying in ' + p.s + ' s · attempt ' + p.n
  },
  'requestSync.popup.retrying': function (p) {
    return 'Retrying… · attempt ' + p.n
  },

  // -- C-11 the "changed underneath you" cue -------------------------------
  'requestSync.cue.changed': function (p) {
    return who(p) + ' changed this' + (p.datetime ? ' on ' + p.datetime : '') + ' while you were editing.'
  },
  'requestSync.cue.changedUnknown': 'Someone else changed this while you were editing.',
  'requestSync.cue.dirty': 'Your edit can\'t replace theirs. When your changes save, their version is shown and yours is listed for you to copy.',
  'requestSync.cue.clean': 'Their version will show when you leave this field.',
  'requestSync.cue.show': 'Show their version',
  'requestSync.cue.hide': 'Hide their version',
  'requestSync.cue.take': 'Use their version',
  'requestSync.cue.close': 'Close',
  'requestSync.cue.iconLabel': function (p) {
    return p.label + ' was changed by ' + (p.by || 'someone else') + ' — show details'
  },
  'requestSync.cue.theirs': 'Theirs:',

  // -- Item 13: Finish Step is navigation only -----------------------------
  'requestSync.finish.hint.manager': function (p) {
    return 'Complete this step to move to Step ' + p.n + '.'
  },
  'requestSync.finish.hint.other': function (p) {
    return 'A manager must complete this step before you can move to Step ' + p.n + '.'
  },

  // -- Item 14: ActionDetails edit of an existing request -------------------
  'requestSync.actionLock.rtb': 'Action type, Backhaul Customer and the backhaul settings are fixed once a request is created. To change them, raise a new request.',
  'requestSync.actionLock.rt': 'Action type and Sharer are fixed once a request is created. To change them, raise a new request.',
  'requestSync.actionLock.nonRt': 'Action type is fixed once an action is created.',
  'requestSync.actionEdit.loadFailed': 'Couldn\'t load the latest version of this action. Please try again.',

  // -- Item 15: the step-5 approval gate ------------------------------------
  'requestSync.step5Gate.note': 'Only an approver or a manager with access to requests can change the final approval.',

  // -- Item 16: telco equipment on RTS --------------------------------------
  'requestSync.equipment.ownRows': 'You can remove only rows your organisation added.',

  // -- Item 17: shared-field notes -------------------------------------------
  'requestSync.sharedNote': function (p) {
    return p.who + ' can also ' + p.action + '. If you both change it at the same time, the first save is kept, and you\'ll be shown your text if yours wasn\'t saved.'
  },
  'requestSync.sharedNote.generalRemarks': function (p) {
    // With a distinct provider: "{Who} and {Tower Co} can also edit these
    // remarks"; without one: "{Tower Co} can also edit these remarks".
    if (p.who) {
      return p.who + ' and ' + p.towerCo + ' can also edit these remarks. If two of you change them at the same time, the first save is kept, and you\'ll be shown your text if yours wasn\'t saved.'
    }
    return p.towerCo + ' can also edit these remarks. If you both change them at the same time, the first save is kept, and you\'ll be shown your text if yours wasn\'t saved.'
  },

  // -- XOL-596 readable read-only remarks (Carlos, 2026-10-14) ----------------------
  // The empty states of the ReadOnlyRemarks block.
  'requestSync.remarks.empty': 'No remarks',
  'requestSync.remarks.emptyGeneral': 'No general remarks',

  // -- XOL-596 completed steps are read-only (Carlos, 2026-10-14) -------------------
  // Placeholders: n = step number, by = completed_by, datetime = completed_at (already
  // formatted), what = a phrase of completed.what.*, towerCo = the Tower Co's name.
  // The step-level hint (CS-H) above the step's form.
  'requestSync.completed.hint.manager': function (p) {
    return (p.by ? 'Step ' + p.n + ' was completed by ' + p.by + (p.datetime ? ' on ' + p.datetime : '') : 'Step ' + p.n + ' is complete') +
      ', so it\'s read-only. To change it, untick Complete Step below.'
  },
  'requestSync.completed.hint.other': function (p) {
    return (p.by ? 'Step ' + p.n + ' was completed by ' + p.by + (p.datetime ? ' on ' + p.datetime : '') : 'Step ' + p.n + ' is complete') +
      ', so it\'s read-only. A manager can re-open it.'
  },
  'requestSync.completed.hint.counterparty': function (p) {
    return 'This step is complete. Uploads and edits are locked until ' + p.towerCo + ' re-opens it.'
  },
  'requestSync.completed.files': function (p) {
    return 'Step ' + p.n + ' is complete, so its files are read-only until it\'s re-opened. You can still download them.'
  },
  'requestSync.completed.sharer': 'Step 3 is complete, so a sharer can\'t be created from this request until the step is re-opened.',
  'requestSync.completed.noBackhaulLines': function (p) {
    return 'No backhaul has been set up for ' + (p.site || 'this site') + '.'
  },
  // Spoken when a step freezes or re-opens because of an adopt (not the user's own command).
  // RTB step 3 (non-CUST) on a completed step with no provider record: no create on a frozen step.
  'requestSync.completed.noBackhaulProvider': 'No backhaul provider has been recorded.',
  'requestSync.completed.announce.frozen': function (p) {
    return p.by
      ? 'Step ' + p.n + ' was completed by ' + p.by + '. It\'s now read-only.'
      : 'Step ' + p.n + ' was completed. It\'s now read-only.'
  },
  'requestSync.completed.announce.reopened': function (p) {
    return 'Step ' + p.n + ' was re-opened. It can be changed again.'
  },
  // The C-12 sections of a 403 step_completed.
  'requestSync.completed.title': function (p) {
    return 'Step ' + p.n + ' is complete'
  },
  'requestSync.completed.lead': function (p) {
    var when = p.datetime ? ' on ' + p.datetime : ''

    if (p.self && p.by) {
      return 'You completed Step ' + p.n + when + ', so ' + p.what + '.'
    }
    if (p.by) {
      return p.by + ' completed Step ' + p.n + when + ', so ' + p.what + '.'
    }
    return 'Step ' + p.n + ' has been completed, so ' + p.what + '.'
  },
  'requestSync.completed.leadCounterparty': function (p) {
    return p.towerCo + ' has completed Step ' + p.n + ', so ' + p.what + '.'
  },
  'requestSync.completed.what.field': 'your change wasn\'t saved',
  'requestSync.completed.what.decision': 'your approval change wasn\'t saved',
  'requestSync.completed.what.upload': 'your file wasn\'t uploaded',
  'requestSync.completed.what.fileRemove': 'the file wasn\'t removed',
  'requestSync.completed.what.categoryRemove': 'the category wasn\'t removed',
  'requestSync.completed.what.categoryRename': 'the category name wasn\'t changed',
  'requestSync.completed.what.categoryAdd': 'the category wasn\'t added',
  'requestSync.completed.what.fileApprove': 'the file approval wasn\'t saved',
  'requestSync.completed.what.fileInfo': 'the additional info wasn\'t saved',
  'requestSync.completed.what.equipmentAdd': 'the equipment wasn\'t added',
  'requestSync.completed.what.equipmentEdit': 'your equipment changes weren\'t saved',
  'requestSync.completed.what.equipmentDelete': 'the equipment wasn\'t deleted',
  'requestSync.completed.what.backhaulAdd': 'the backhaul line wasn\'t added',
  'requestSync.completed.what.backhaulEdit': 'your backhaul changes weren\'t saved',
  'requestSync.completed.what.backhaulCustomer': 'the backhaul customer wasn\'t saved',
  'requestSync.completed.what.sharer': 'the sharer wasn\'t created',
  'requestSync.completed.what.lock': 'the approval wasn\'t locked',
  'requestSync.completed.what.unlock': 'the approval wasn\'t unlocked',
  // What to do next: kind 'change' (default) | 'lock' | 'unlock'.
  'requestSync.completed.footer': function (p) {
    if (p.counterparty) {
      return 'Ask ' + p.towerCo + ' to re-open it if this still needs to change.'
    }
    if (!p.canReopen) {
      return 'A manager can re-open Step ' + p.n + ' if this still needs to change.'
    }
    if (p.kind === 'lock') {
      return 'To lock it, re-open Step ' + p.n + ' (untick Complete Step) first.'
    }
    if (p.kind === 'unlock') {
      return 'To unlock it, re-open Step ' + p.n + ' (untick Complete Step) first.'
    }
    return 'To change it, re-open Step ' + p.n + ' (untick Complete Step), then make the change again.'
  },

  // The confirms of Complete Step (two-button) and Re-open.
  'requestSync.completeStep.confirm.body': function (p) {
    return 'Once Step ' + p.n + ' is completed, it becomes read-only for everyone, including you. Nothing in it can be changed, and no files can be uploaded, until a manager re-opens it. Continue?'
  },
  'requestSync.completeStep.confirm.ok': 'Complete',
  'requestSync.completeStep.confirm.cancel': 'Cancel',
  'requestSync.reopenStep.confirm.title': 'Re-open this step?',
  'requestSync.reopenStep.confirm.body': function (p) {
    return 'Step ' + p.n + ' will be marked incomplete and become editable again for everyone, including its files.' +
      (p.locked ? ' Its approval stays locked; unlock it separately if it needs to change.' : '') + ' Continue?'
  },
  'requestSync.reopenStep.confirm.ok': 'Re-open',
  'requestSync.reopenStep.confirm.cancel': 'Cancel'
}

// requestSync.field.<kind>.<path> for every mapped path, so a spec (or a
// caller that wants the raw template) can reach a label by its key.
Object.keys(STEP_LABELS).forEach(function (path) {
  COPY['requestSync.field.step.' + path] = STEP_LABELS[path]
})
Object.keys(ACTION_LABELS).forEach(function (path) {
  COPY['requestSync.field.action.' + path] = ACTION_LABELS[path]
})

/**
 * The text for a copy key. A template function receives `params`; a plain
 * string ignores them. An unknown key is a bug: it is logged and the key is
 * returned, so the screen shows something findable instead of throwing.
 */
export function copyText (key, params) {
  if (!Object.prototype.hasOwnProperty.call(COPY, key)) {
    console.error('requestSyncCopy: unknown copy key', key)
    return key
  }

  var value = COPY[key]

  return typeof value === 'function' ? value(params || {}) : value
}

/** Every copy key (specs assert the table is complete). */
export function copyKeys () {
  return Object.keys(COPY)
}

export default {
  COPY: COPY,
  copyText: copyText,
  copyKeys: copyKeys,
  fieldLabel: fieldLabel
}
